import { api } from "../http";
import { buildReferenceCacheKey, isNavigatorOffline, searchCachedReferences } from "../../offline/referenceCache";

export type CargoReferenceType =
  | "suppliers"
  | "receivers"
  | "manufacturers"
  | "brands"
  | "products"
  | "laboratories"
  | "fumigation-providers"
  | "rapid-test-kits";

export type CargoReferenceRecord = {
  id: string;
  name: string;
  attributes?: Record<string, string> | null;
};

export type CargoReferencePayload = Record<string, string | undefined | null>;

export type CargoReferenceService = {
  search: (type: CargoReferenceType, params: { q?: string; limit?: number }) => Promise<CargoReferenceRecord[]>;
  create: (type: CargoReferenceType, payload: CargoReferencePayload) => Promise<CargoReferenceRecord>;
  update: (type: CargoReferenceType, id: string, payload: CargoReferencePayload) => Promise<CargoReferenceRecord>;
};
const BASE_PATH = "/api/cargo/references";

function buildQuery(params: Record<string, unknown>) {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join("&");
}

const prefixFor = (type: CargoReferenceType) => buildReferenceCacheKey("cargo", type);

function path(type: CargoReferenceType) {
  return `${BASE_PATH}/${type}`;
}

export const cargoReferencesApi = {
  async search(type: CargoReferenceType, params: { q?: string; limit?: number } = {}) {
    if (isNavigatorOffline()) {
      const cached = await searchCachedReferences(prefixFor(type), params);
      if (cached) {
        return cached as CargoReferenceRecord[];
      }
    }
    try {
      const query = buildQuery(params);
      return await api.get<CargoReferenceRecord[]>(`${path(type)}${query ? `?${query}` : ""}`);
    } catch (error) {
      const cached = await searchCachedReferences(prefixFor(type), params);
      if (cached) {
        return cached as CargoReferenceRecord[];
      }
      throw error;
    }
  },
  async create(type: CargoReferenceType, payload: CargoReferencePayload) {
    return api.post<CargoReferenceRecord, CargoReferencePayload>(path(type), payload);
  },
  async update(type: CargoReferenceType, id: string, payload: CargoReferencePayload) {
    return api.put<CargoReferenceRecord, CargoReferencePayload>(
      `${path(type)}/${encodeURIComponent(id)}`,
      payload,
    );
  },
};
